import React, { ReactElement, SVGAttributes } from 'react'

import { Color } from '../../enums/color'
import { ColorOrCurrent } from '../../types/colorOrCurrent'
import { iconToComponent } from './iconToComponent'
import { IconSize } from './types'

// Convert size string to number for Lucide icons
export const iconSizeToNumber: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
}

type IconSVGProps = Omit<SVGAttributes<unknown>, 'width' | 'height'>

type LucideIconProps = {
  size?: number | string
  color?: string
  [key: string]: unknown
}

const colorToString = (color: ColorOrCurrent): string => {
  if (color === 'currentColor') {
    return 'currentColor'
  }

  return String(color)
}

export type IconName = keyof typeof iconToComponent | ReactElement<IconSVGProps>

export interface IconProps extends Omit<IconSVGProps, 'size' | 'color'> {
  color?: ColorOrCurrent
  icon: IconName
  size?: IconSize
}

export const Icon = ({ size = 'sm', color, icon, ...rest }: IconProps): ReactElement | null => {
  const isIconName = typeof icon === 'string'
  const IconComponent = (isIconName ? iconToComponent[icon] : icon) as React.ComponentType<LucideIconProps>

  if (!IconComponent) {
    return null
  }

  const sizeNumber = iconSizeToNumber[size]
  const finalColor = color || Color.Grey500
  const colorString = colorToString(finalColor)

  return (
    <IconComponent size={sizeNumber} color={colorString} {...(isIconName ? { 'data-testid': icon } : {})} {...rest} />
  )
}
